import { useTemplatesApi } from '@/composables/templates/useTemplatesApi';
import { sendingChannelBoxes, getTemplateAndRedirect } from '@/composables/flows/useSendingChannelForm';
import type { FlowStructureDto } from '@/types/flow-structure';

/** Mapping operator type → template channel type (inverse of AutomatedScenarios.ts:80-89) */
const operatorToTemplateType: Record<string, string> = {
  boxsendemail: 'email',
  boxsendsms: 'sms',
  boxsendrcs: 'richtexting',
  boxsendwhatsapp: 'whatsapp',
  boxsendfbmessenger: 'facebookmessenger',
  boxsendpushnotification: 'pushnotifications',
  boxdisplayfreepopup: 'popup',
  boxdisplayfreemodel: 'embed',
};

const GAME_TYPES = ['wheel', 'scratchCard', 'slotMachine'];

export interface TemplateRenamePreview {
  templateId: number;
  newName: string;
  isRedirect: boolean;
}

/**
 * Extract game prize templateId → label mapping from a template's config.
 * games_data structure: { [gameType]: { prizes: [{ label, templateId, ... }] } }
 * templateId can be a number or base64-encoded string.
 */
function extractGamePrizeMap(templateConfig: string | null): Map<number, string> {
  const map = new Map<number, string>();
  if (!templateConfig) return map;

  try {
    const config = JSON.parse(templateConfig);
    const gamesData = config?.games_data;
    if (!gamesData) return map;

    for (const gameType of GAME_TYPES) {
      const gameConfig = gamesData[gameType];
      if (!gameConfig?.prizes || !Array.isArray(gameConfig.prizes)) continue;

      for (let i = 0; i < gameConfig.prizes.length; i++) {
        const prize = gameConfig.prizes[i];
        if (!prize.templateId) continue;

        let id: number;
        if (typeof prize.templateId === 'number') {
          id = prize.templateId;
        } else {
          try {
            id = Number(atob(String(prize.templateId)));
          } catch {
            id = Number(prize.templateId);
          }
        }

        if (id > 0) {
          map.set(id, prize.label || `Prize ${i + 1}`);
        }
      }
    }
  } catch {
    // Invalid config, return empty map
  }

  return map;
}

/**
 * Build a preview list of template renames for a flow.
 * Uses the same naming patterns as auto-naming:
 *   Primary:    "{Channel} #{n}"                     → "E-mails #1"
 *   Redirect:   "{ParentName} - {Channel} #{n}"      → "E-mails #1 - SMS #1"
 *   Game prize: "{ParentName} - {PrizeLabel}"         → "Popin #1 - Gros lot"
 */
export async function buildTemplateRenamePreview(
  flow: FlowStructureDto,
  idShop: number,
  t: (key: string) => string,
): Promise<TemplateRenamePreview[]> {
  const previews: TemplateRenamePreview[] = [];
  const seenTemplateIds = new Set<number>();
  const counters: Record<string, number> = {};

  // 1. Collect primary templates from flow steps
  const primaryTemplates: { templateId: number; stepType: string; primaryName: string }[] = [];

  for (const step of flow.steps) {
    if (!sendingChannelBoxes.includes(step.type)) continue;

    const templateId = Number(step.parameters?.template_display_selector_id);
    if (!templateId || templateId <= 0) continue;
    if (seenTemplateIds.has(templateId)) continue;
    seenTemplateIds.add(templateId);

    const channelType = operatorToTemplateType[step.type] || step.type;
    counters[step.type] = (counters[step.type] || 0) + 1;
    const primaryName = `${t(`templates.channels.${channelType}`)} #${counters[step.type]}`;

    previews.push({ templateId, newName: primaryName, isRedirect: false });
    primaryTemplates.push({ templateId, stepType: step.type, primaryName });
  }

  // 2. Fetch redirect chains for each primary template
  for (const primary of primaryTemplates) {
    try {
      const chain = await getTemplateAndRedirect(idShop, primary.templateId);
      if (!chain || chain.length <= 1) continue;

      // Build game prize mapping from ALL templates in the chain:
      //   prizeId → { label, ownerTemplateId }
      // games_data lives on the game popup template, not the root email
      const gamePrizeMap = new Map<number, { label: string; ownerId: number }>();
      for (const tmpl of chain) {
        if (!tmpl) continue;
        const prizeMap = extractGamePrizeMap(tmpl.template_config ?? null);
        prizeMap.forEach((label, id) => gamePrizeMap.set(id, { label, ownerId: tmpl.id_template }));
      }

      // Track assigned names: templateId → newName (so game prizes can reference their parent's name)
      const assignedNames = new Map<number, string>();
      assignedNames.set(primary.templateId, primary.primaryName);

      // Redirect counter per channel type (for non-game redirects)
      const redirectCounters: Record<string, number> = {};

      // chain[0] is the primary, chain[1+] are redirects/game prizes
      for (let i = 1; i < chain.length; i++) {
        const redirect = chain[i];
        if (!redirect || seenTemplateIds.has(redirect.id_template)) continue;
        seenTemplateIds.add(redirect.id_template);

        // Check if this is a game prize template
        const prizeInfo = gamePrizeMap.get(redirect.id_template);
        if (prizeInfo) {
          // Game prize format: "{GameTemplateName} - {PrizeLabel}"
          // Use the assigned name of the game template that owns this prize
          const ownerName = assignedNames.get(prizeInfo.ownerId) || primary.primaryName;
          const redirectName = `${ownerName} - ${prizeInfo.label}`;
          previews.push({ templateId: redirect.id_template, newName: redirectName, isRedirect: true });
          assignedNames.set(redirect.id_template, redirectName);
        } else {
          // Standard redirect format: "{ParentName} - {Channel} #{n}"
          const redirectChannelType = redirect.type || 'email';
          redirectCounters[redirectChannelType] = (redirectCounters[redirectChannelType] || 0) + 1;
          const redirectName = `${primary.primaryName} - ${t(`templates.channels.${redirectChannelType}`)} #${redirectCounters[redirectChannelType]}`;
          previews.push({ templateId: redirect.id_template, newName: redirectName, isRedirect: true });
          assignedNames.set(redirect.id_template, redirectName);
        }
      }
    } catch {
      // If chain fetch fails for one template, continue with others
    }
  }

  return previews;
}

/**
 * Execute the batch rename, collecting errors rather than failing fast.
 */
export async function executeTemplateRenames(
  previews: TemplateRenamePreview[],
): Promise<{ success: boolean; errors: string[] }> {
  const { renameTemplate } = useTemplatesApi();
  const errors: string[] = [];

  for (const preview of previews) {
    const result = await renameTemplate(preview.templateId, preview.newName);
    if (!result.success) {
      errors.push(`Template #${preview.templateId}: ${result.message || 'error'}`);
    }
  }

  return { success: errors.length === 0, errors };
}
